/**
 * Task Import Vuex Getters
 * Computed state accessors for the task import feature.
 */

import { WIZARD_STEPS, calculateProgress } from '../config/taskImportSteps';
import { getSystemFields } from '../config/taskSystemFields';

export default {
  // ==========================================================================
  // Wizard State
  // ==========================================================================

  GET_WIZARD_OPEN: state => state.wizardOpen,
  GET_WIZARD_STEP: state => state.wizardStep,

  /**
   * Calculate overall wizard progress percentage.
   */
  GET_WIZARD_PROGRESS: state => {
    return calculateProgress(state.wizardStep);
  },

  /**
   * Check if on specific wizard steps.
   */
  IS_CONFIGURATION_STEP: state => state.wizardStep === WIZARD_STEPS.CONFIGURATION,
  IS_FIELD_MAPPING_STEP: state => state.wizardStep === WIZARD_STEPS.FIELD_MAPPING,
  IS_REVIEW_STEP: state => state.wizardStep === WIZARD_STEPS.REVIEW,
  IS_SUCCESS_STEP: state => state.wizardStep === WIZARD_STEPS.SUCCESS,

  // ==========================================================================
  // Import Mode
  // ==========================================================================

  GET_IMPORT_MODE: state => state.importMode,
  IS_EXISTING_BOARD_MODE: state => state.importMode === 'existing',
  IS_NEW_BOARD_MODE: state => state.importMode === 'new',

  // ==========================================================================
  // Scope Selection
  // ==========================================================================

  GET_SCOPE_TYPE: state => state.scopeType,
  GET_SELECTED_FACILITY_ID: state => state.selectedFacilityId,
  GET_SELECTED_FACILITY_GROUP_ID: state => state.selectedFacilityGroupId,

  /**
   * Get the effective target ID based on scope type.
   */
  GET_EFFECTIVE_TARGET_ID: state => {
    return state.scopeType === 'Facility'
      ? state.selectedFacilityId
      : state.selectedFacilityGroupId;
  },

  /**
   * Check if scope is configured.
   */
  IS_SCOPE_CONFIGURED: state => {
    return state.scopeType === 'Facility'
      ? !!state.selectedFacilityId
      : !!state.selectedFacilityGroupId;
  },

  // ==========================================================================
  // TaskBoard Selection
  // ==========================================================================

  GET_SELECTED_TASK_TYPE_ID: state => state.selectedTaskTypeId,
  GET_SELECTED_TASK_TYPE: state => state.selectedTaskType,
  GET_AVAILABLE_TASK_TYPES: state => state.availableTaskTypes,
  GET_TASK_TYPES_LOADING: state => state.taskTypesLoading,
  GET_BOARD_SELECTION_DISABLED: state => state.boardSelectionDisabled,

  /**
   * Get custom fields from selected TaskBoard schema.
   */
  GET_TASK_TYPE_CUSTOM_FIELDS: state => {
    if (!state.selectedTaskType?.customFields) {
      return [];
    }
    return state.selectedTaskType.customFields;
  },

  /**
   * Get statuses from selected TaskBoard.
   */
  GET_TASK_TYPE_STATUSES: state => {
    if (!state.selectedTaskType?.statuses) {
      return [];
    }
    return state.selectedTaskType.statuses;
  },

  // ==========================================================================
  // New TaskBoard
  // ==========================================================================

  GET_NEW_BOARD_NAME: state => state.newBoardName,
  GET_NEW_BOARD_DESCRIPTION: state => state.newBoardDescription,

  // ==========================================================================
  // Data Source Type
  // ==========================================================================

  GET_DATA_SOURCE_TYPE: state => state.dataSourceType,
  IS_API_SOURCE: state => state.dataSourceType === 'api',
  IS_EXCEL_SOURCE: state => state.dataSourceType === 'excel',

  // ==========================================================================
  // Inline API Configuration
  // ==========================================================================

  GET_API_CONFIG: state => state.apiConfig,
  GET_API_TEST_RESULT: state => state.apiTestResult,
  GET_API_PREVIEW_DATA: state => state.apiPreviewData,
  IS_API_TESTED: state => !!(state.apiTestResult && state.apiTestResult.success),
  HAS_API_PREVIEW: state => !!(state.apiPreviewData && state.apiPreviewData.headers && state.apiPreviewData.headers.length > 0),

  // ==========================================================================
  // File Data
  // ==========================================================================

  GET_PARSED_DATA: state => state.parsedData,
  GET_EXCEL_COLUMNS: state => state.excelColumns,
  GET_SOURCE_FILE_NAME: state => state.sourceFileName,
  GET_PARSED_DATA_COUNT: state => state.parsedData.length,

  // ==========================================================================
  // Field Mappings
  // ==========================================================================

  GET_SYSTEM_FIELD_MAPPINGS: state => state.systemFieldMappings,
  GET_CUSTOM_FIELD_MAPPINGS: state => state.customFieldMappings,

  /**
   * Get system fields with mapping info for UI display.
   */
  GET_SYSTEM_FIELDS_WITH_MAPPINGS: state => {
    const systemFields = getSystemFields();
    return systemFields.map(field => ({
      ...field,
      mappedColumn: state.systemFieldMappings[field.key] || null,
      isMapped: !!state.systemFieldMappings[field.key],
    }));
  },

  /**
   * Get count of mapped system fields.
   */
  GET_MAPPED_SYSTEM_FIELDS_COUNT: state => {
    return Object.values(state.systemFieldMappings).filter(Boolean).length;
  },

  /**
   * Get count of mapped custom fields.
   * In existing mode: counts from customFieldMappings
   * In new mode: counts section fields with excelColumn mapped
   */
  GET_MAPPED_CUSTOM_FIELDS_COUNT: state => {
    if (state.importMode === 'existing') {
      // Existing mode: count from customFieldMappings
      return Object.values(state.customFieldMappings).filter(Boolean).length;
    } else {
      // New mode: count section fields with excelColumn mapped
      let count = 0;
      state.taskBoardSections.forEach(section => {
        (section.fields || []).forEach(field => {
          if (field.excelColumn) count++;
        });
      });
      return count;
    }
  },

  /**
   * Get all mapped columns (system, custom, and section field mappings).
   */
  GET_ALL_MAPPED_COLUMNS: state => {
    const systemMapped = Object.values(state.systemFieldMappings).filter(Boolean);
    const customMapped = Object.values(state.customFieldMappings).filter(Boolean);

    // Also include section field mappings (for new board mode)
    const sectionMapped = [];
    if (state.importMode === 'new') {
      state.taskBoardSections.forEach(section => {
        (section.fields || []).forEach(field => {
          if (field.excelColumn) {
            sectionMapped.push(field.excelColumn);
          }
        });
      });
    }

    return [...new Set([...systemMapped, ...customMapped, ...sectionMapped])];
  },

  /**
   * Get unmapped Excel columns.
   */
  GET_UNMAPPED_COLUMNS: (state, getters) => {
    const mapped = getters.GET_ALL_MAPPED_COLUMNS;
    return state.excelColumns.filter(col => !mapped.includes(col));
  },

  /**
   * Calculate mapping progress percentage.
   * Uses mode-appropriate field totals for accurate progress calculation.
   */
  GET_MAPPING_PROGRESS: (state, getters) => {
    const systemFields = getSystemFields();

    // Get custom fields total based on import mode
    let customFieldsTotal = 0;
    if (state.importMode === 'existing') {
      customFieldsTotal = getters.GET_TASK_TYPE_CUSTOM_FIELDS?.length || 0;
    } else {
      // Count total fields across all sections in new mode
      state.taskBoardSections.forEach(section => {
        customFieldsTotal += (section.fields || []).length;
      });
    }

    const totalCount = systemFields.length + customFieldsTotal;
    if (totalCount === 0) return 0;

    const mappedCount = getters.GET_MAPPED_SYSTEM_FIELDS_COUNT + getters.GET_MAPPED_CUSTOM_FIELDS_COUNT;
    return Math.round((mappedCount / totalCount) * 100);
  },

  // ==========================================================================
  // New Custom Fields
  // ==========================================================================

  GET_NEW_CUSTOM_FIELDS: state => state.newCustomFields,
  GET_SECTIONS: state => state.sections,

  // ==========================================================================
  // TaskBoard Sections Configuration (for Configure TaskBoard dialog)
  // ==========================================================================

  GET_TASKBOARD_SECTIONS: state => state.taskBoardSections,

  /**
   * Get count of configured sections.
   */
  GET_CONFIGURED_SECTIONS_COUNT: state => state.taskBoardSections.length,

  /**
   * Get total count of configured fields across all sections.
   */
  GET_CONFIGURED_FIELDS_COUNT: state => {
    return state.taskBoardSections.reduce((sum, section) => sum + (section.fields?.length || 0), 0);
  },

  /**
   * Get all field mappings from configured sections.
   * Returns { excelColumn: fieldKey } for use in import.
   */
  GET_SECTION_FIELD_MAPPINGS: state => {
    const mappings = {};
    state.taskBoardSections.forEach((section, sIndex) => {
      (section.fields || []).forEach((field, fIndex) => {
        if (field.excelColumn) {
          mappings[field.excelColumn] = `section_${sIndex}_field_${fIndex}`;
        }
      });
    });
    return mappings;
  },

  // ==========================================================================
  // Validation
  // ==========================================================================

  /**
   * Check if required system fields are mapped.
   * For Task Import, 'text' (title) is the only required field.
   */
  IS_MAPPING_VALID: state => {
    return !!state.systemFieldMappings['text'];
  },

  /**
   * Get valid rows (rows with required field values).
   */
  GET_VALID_ROWS: state => {
    const titleColumn = state.systemFieldMappings['text'];
    if (!titleColumn) {
      return state.parsedData;
    }

    return state.parsedData.filter(row => {
      const titleValue = row[titleColumn];
      return titleValue !== undefined && titleValue !== null && String(titleValue).trim() !== '';
    });
  },

  /**
   * Get count of valid rows.
   */
  GET_VALID_ROWS_COUNT: (state, getters) => {
    return getters.GET_VALID_ROWS.length;
  },

  /**
   * Get count of filtered (invalid) rows.
   */
  GET_FILTERED_ROWS_COUNT: (state, getters) => {
    return state.parsedData.length - getters.GET_VALID_ROWS.length;
  },

  /**
   * Check if wizard can proceed to next step.
   */
  CAN_PROCEED_TO_STEP: (state, getters) => step => {
    switch (step) {
      case WIZARD_STEPS.FIELD_MAPPING: { // From Config to Field Mapping
        const hasData = state.dataSourceType === 'api'
          ? getters.HAS_API_PREVIEW
          : state.parsedData.length > 0;
        if (state.importMode === 'existing') {
          return getters.IS_SCOPE_CONFIGURED &&
                 !!state.selectedTaskTypeId &&
                 hasData;
        } else {
          return getters.IS_SCOPE_CONFIGURED &&
                 !!state.newBoardName.trim() &&
                 hasData;
        }
      }

      case WIZARD_STEPS.REVIEW: // From Field Mapping to Review
        return getters.IS_MAPPING_VALID;

      case WIZARD_STEPS.SUCCESS: // From Review to Success (import triggered)
        return getters.IS_MAPPING_VALID && getters.GET_VALID_ROWS_COUNT > 0;

      default:
        return true;
    }
  },

  // ==========================================================================
  // Duplicate Handling
  // ==========================================================================

  GET_KEY_COLUMN: state => state.keyColumn,
  GET_DUPLICATE_CHECK_RESULT: state => state.duplicateCheckResult,
  GET_DUPLICATE_HANDLING: state => state.duplicateHandling,

  /**
   * Check if there are duplicates to handle.
   */
  HAS_DUPLICATES: state => {
    return state.duplicateCheckResult?.duplicatesFound > 0;
  },

  // ==========================================================================
  // User Email Resolution
  // ==========================================================================

  GET_USER_EMAIL_MAPPINGS: state => state.userEmailMappings,
  GET_UNRESOLVED_USER_EMAILS: state => state.unresolvedUserEmails,
  GET_RESOLVING_USERS: state => state.resolvingUsers,

  /**
   * Check if there are unresolved user emails (for showing warnings).
   */
  HAS_UNRESOLVED_USERS: state => {
    return state.unresolvedUserEmails.length > 0;
  },

  /**
   * Get count of unresolved user emails.
   */
  GET_UNRESOLVED_USERS_COUNT: state => state.unresolvedUserEmails.length,

  // ==========================================================================
  // Loading States
  // ==========================================================================

  GET_LOADING: state => state.loading,
  GET_IMPORTING: state => state.importing,
  GET_CHECKING_DUPLICATES: state => state.checkingDuplicates,
  GET_EXPORTING: state => state.exporting,
  GET_HISTORY_LOADING: state => state.historyLoading,

  /**
   * Check if any operation is in progress.
   */
  IS_BUSY: state => {
    return state.loading || state.importing || state.checkingDuplicates || state.exporting || state.resolvingUsers;
  },

  // ==========================================================================
  // Error State
  // ==========================================================================

  GET_ERROR: state => state.error,

  // ==========================================================================
  // Import Result
  // ==========================================================================

  GET_IMPORT_RESULT: state => state.importResult,

  /**
   * Get import statistics from result.
   */
  GET_IMPORT_STATS: state => {
    if (!state.importResult) return null;

    return {
      total: state.importResult.totalRecords || 0,
      created: state.importResult.createdCount || 0,
      updated: state.importResult.updatedCount || 0,
      skipped: state.importResult.skippedCount || 0,
      failed: state.importResult.failedCount || 0,
      hasErrors: (state.importResult.errors?.length || 0) > 0,
      status: state.importResult.status,
    };
  },

  // ==========================================================================
  // Import History Sidebar
  // ==========================================================================

  /**
   * Check if history sidebar is open.
   */
  IS_HISTORY_SIDEBAR_OPEN: state => state.historySidebarOpen,

  /**
   * Get current history filters.
   */
  GET_HISTORY_FILTERS: state => state.historyFilters,

  /**
   * Get import history items.
   */
  GET_IMPORT_HISTORY: state => state.importHistory,

  /**
   * Get total count of import history items.
   */
  GET_IMPORT_HISTORY_TOTAL: state => state.importHistoryTotal,

  /**
   * Check if there are more history items to load.
   */
  HAS_MORE_HISTORY: state => {
    return state.importHistory.length < state.importHistoryTotal;
  },

  /**
   * Get count of remaining history items to load.
   */
  GET_REMAINING_HISTORY_COUNT: state => {
    return Math.max(0, state.importHistoryTotal - state.importHistory.length);
  },

  // ==========================================================================
  // Export
  // ==========================================================================

  GET_EXPORT_RESULT: state => state.exportResult,

  // ==========================================================================
  // API Data Sources
  // ==========================================================================

  GET_API_DATA_SOURCES: state => state.apiDataSources,
  GET_API_DATA_SOURCES_TOTAL: state => state.apiDataSourcesTotal,
  GET_API_DATA_SOURCE_LOADING: state => state.apiDataSourceLoading,

  GET_API_DATA_SOURCES_BY_TASK_TYPE: state => (taskTypeId) => {
    return state.apiDataSources.filter(ds => ds.taskTypeId === taskTypeId);
  },

  // ==========================================================================
  // API Execution History
  // ==========================================================================

  GET_API_EXECUTION_HISTORY: state => state.apiExecutionHistory,
  GET_API_EXECUTION_HISTORY_TOTAL: state => state.apiExecutionHistoryTotal,
  GET_API_EXECUTION_HISTORY_LOADING: state => state.apiExecutionHistoryLoading,

  // ==========================================================================
  // Preview Data
  // ==========================================================================

  /**
   * Get preview data for review step.
   * Returns first N rows with mapped field values.
   */
  GET_PREVIEW_DATA: (state, getters) => (limit = 10) => {
    const validRows = getters.GET_VALID_ROWS;
    const previewRows = validRows.slice(0, limit);

    return previewRows.map(row => {
      const mapped = {};

      // Map system fields
      Object.entries(state.systemFieldMappings).forEach(([fieldKey, excelCol]) => {
        if (excelCol && row[excelCol] !== undefined) {
          mapped[fieldKey] = row[excelCol];
        }
      });

      // Map custom fields
      Object.entries(state.customFieldMappings).forEach(([fieldId, excelCol]) => {
        if (excelCol && row[excelCol] !== undefined) {
          mapped[`custom_${fieldId}`] = row[excelCol];
        }
      });

      return mapped;
    });
  },
};
